/**
 * Ads List Page with v0.7.0 Advanced Search
 * Features: Pagination, multi-select filters, image filter, facets, URL sync
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import DesktopLayout from '../../components/layout/DesktopLayout';
import AdvancedSearch from '../../components/search/AdvancedSearch';
import SearchFacets from '../../components/search/SearchFacets';
import AdCardList from '../../components/advertising/AdCardList';
import EmptyState from '../../components/common/EmptyState';
import Modal from '../../components/common/Modal';
import AdDetailModal from '../../components/advertising/AdDetailModal/AdDetailModal';
import AdFormModal from '../../components/advertising/AdFormModal';
import api from '../../services/api';
import { getMemberUsage } from '../../services/membersApi';
import type { Ad, AdvancedSearchParams, SearchFacetsData } from '../../types';
import './AdsListPage.css';

// Plan limits configuration
const PLAN_LIMITS: Record<string, { maxAds: number | null }> = {
  free: { maxAds: 0 },
  basic: { maxAds: 10 },
  premium: { maxAds: null },
};

const AdsListPage = () => {
  const navigate = useNavigate();
  const [urlSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [ads, setAds] = useState<Ad[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [searchParams, setSearchParams] = useState<AdvancedSearchParams>({});
  const [facets, setFacets] = useState<SearchFacetsData | null>(null);

  // Usage and limit state
  const [usageStats, setUsageStats] = useState({ currentAds: 0, currentContacts: 0 });
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [limitReason, setLimitReason] = useState('');

  // Pagination state
  const [page, setPage] = useState(0);
  const limit = 20; // 每頁顯示 20 筆

  // Modal and panel state (desktop only)
  const [showAdvancedSearchModal, setShowAdvancedSearchModal] = useState(false);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [showAdDetailModal, setShowAdDetailModal] = useState(false);
  const [showAdFormModal, setShowAdFormModal] = useState(false);
  const [editingAdId, setEditingAdId] = useState<string | null>(null);
  const [selectedAdId, setSelectedAdId] = useState<string | null>(null);

  // Get all ad IDs for navigation
  const allAdIds = ads.map(ad => ad.id);

  // Parse URL params on initial load and when URL search params change
  useEffect(() => {
    const params: AdvancedSearchParams = {};

    // Parse URL search parameters
    const category = urlSearchParams.get('category');
    const categories = urlSearchParams.get('categories');
    const region = urlSearchParams.get('region');
    const regions = urlSearchParams.get('regions');
    const district = urlSearchParams.get('district');
    const keyword = urlSearchParams.get('keyword');
    const hasImage = urlSearchParams.get('hasImage');
    const sortBy = urlSearchParams.get('sortBy') as 'price' | 'date' | 'popularity' | 'relevance' | null;
    const sortOrder = urlSearchParams.get('sortOrder') as 'asc' | 'desc' | null;
    const priceMin = urlSearchParams.get('priceMin');
    const priceMax = urlSearchParams.get('priceMax');
    const pageParam = urlSearchParams.get('page');

    if (category) params.category = category;
    if (categories) params.categories = categories.split(',').filter(c => c.length > 0);
    if (region) params.region = region;
    if (regions) params.regions = regions.split(',').filter(r => r.length > 0);
    if (district) params.district = district;
    if (keyword) params.keyword = keyword;
    if (hasImage === 'true') params.hasImage = true;
    if (sortBy) params.sortBy = sortBy;
    if (sortOrder) params.sortOrder = sortOrder;
    if (priceMin || priceMax) {
      params.priceRange = {
        min: priceMin ? parseInt(priceMin, 10) : null,
        max: priceMax ? parseInt(priceMax, 10) : null,
      };
    }
    if (pageParam) {
      setPage(Math.max(0, parseInt(pageParam, 10) - 1)); // Convert 1-based to 0-based
    }

    // Set default sort if not provided
    if (!params.sortBy) {
      params.sortBy = 'date';
      params.sortOrder = 'desc';
    }

    setSearchParams(params);
  }, [urlSearchParams]); // Re-run when URL search params change

  useEffect(() => {
    loadAds();
  }, [searchParams, page]);

  // Fetch usage stats when user is available
  const fetchUsageStats = useCallback(async () => {
    if (!user) return;
    try {
      const usage = await getMemberUsage();
      if (usage) {
        setUsageStats({
          currentAds: usage.currentAds,
          currentContacts: usage.currentContacts,
        });
      }
    } catch (err) {
      console.error('Failed to fetch usage stats:', err);
    }
  }, [user]);

  useEffect(() => {
    fetchUsageStats();
  }, [fetchUsageStats]);

  // Open modal if id is provided in query string
  useEffect(() => {
    const adId = urlSearchParams.get('id');
    if (adId && window.innerWidth >= 768) {
      setSelectedAdId(adId);
      setShowAdDetailModal(true);
      // Clear the id from URL to prevent reopening on refresh
      const params = new URLSearchParams(urlSearchParams);
      params.delete('id');
      const queryString = params.toString();
      const newUrl = queryString ? `/ads?${queryString}` : '/ads';
      window.history.replaceState({}, '', newUrl);
    }
  }, []);

  const loadAds = async () => {
    setLoading(true);
    try {
      // Use advanced search endpoint if we have advanced filters
      const hasAdvancedFilters =
        searchParams.category ||
        searchParams.categories ||
        searchParams.region ||
        searchParams.regions ||
        searchParams.district ||
        searchParams.priceRange ||
        searchParams.keyword ||
        searchParams.hasImage ||
        searchParams.sortBy;

      const endpoint = hasAdvancedFilters ? '/api/ads/search' : '/api/ads';

      // Convert searchParams to flat query params
      const queryParams: Record<string, string | number | boolean | undefined> = {
        category: searchParams.category,
        // Convert categories array to comma-separated string
        categories: searchParams.categories?.join(','),
        region: searchParams.region,
        // Convert regions array to comma-separated string
        regions: searchParams.regions?.join(','),
        district: searchParams.district,
        keyword: searchParams.keyword,
        hasImage: searchParams.hasImage,
        sortBy: searchParams.sortBy,
        sortOrder: searchParams.sortOrder,
        priceMin: searchParams.priceRange?.min ?? undefined,
        priceMax: searchParams.priceRange?.max ?? undefined,
        // Pagination params
        limit,
        offset: page * limit,
      };

      const response = await api.get<Ad[]>(endpoint, queryParams);

      if (response.success) {
        // response.data is the array of ads for paginated responses
        setAds(Array.isArray(response.data) ? response.data : []);
        setTotal(response.pagination?.total || (Array.isArray(response.data) ? response.data.length : 0));
        // Store facets data from response
        setFacets(response.facets || null);
      } else {
        setAds([]);
        setTotal(0);
        setFacets(null);
      }
    } catch (err) {
      console.error('Error loading ads:', err);
      setAds([]);
      setTotal(0);
      setFacets(null);
    } finally {
      setLoading(false);
    }
  };

  // Update URL when search params change
  useEffect(() => {
    const params = new URLSearchParams();

    if (searchParams.category) params.set('category', searchParams.category);
    if (searchParams.categories && searchParams.categories.length > 0) {
      params.set('categories', searchParams.categories.join(','));
    }
    if (searchParams.region) params.set('region', searchParams.region);
    if (searchParams.regions && searchParams.regions.length > 0) {
      params.set('regions', searchParams.regions.join(','));
    }
    if (searchParams.district) params.set('district', searchParams.district);
    if (searchParams.keyword) params.set('keyword', searchParams.keyword);
    if (searchParams.hasImage) params.set('hasImage', 'true');
    if (searchParams.sortBy) params.set('sortBy', searchParams.sortBy);
    if (searchParams.sortOrder) params.set('sortOrder', searchParams.sortOrder);
    if (searchParams.priceRange?.min !== null && searchParams.priceRange?.min !== undefined) {
      params.set('priceMin', String(searchParams.priceRange.min));
    }
    if (searchParams.priceRange?.max !== null && searchParams.priceRange?.max !== undefined) {
      params.set('priceMax', String(searchParams.priceRange.max));
    }
    // Add page to URL (1-based for display)
    if (page > 0) {
      params.set('page', String(page + 1));
    }

    // Update URL without triggering navigation
    const queryString = params.toString();
    const newUrl = queryString ? `/ads?${queryString}` : '/ads';
    window.history.replaceState({}, '', newUrl);
  }, [searchParams, page]);

  const handleSearch = (params: AdvancedSearchParams) => {
    setSearchParams(params);
    setPage(0); // 重置到第一頁
    setShowFilterPanel(false); // 關閉篩選面板
  };

  const handleAdClick = (adId: string) => {
    // Desktop: open modal, Mobile: navigate
    if (window.innerWidth >= 768) {
      setSelectedAdId(adId);
      setShowAdDetailModal(true);
    } else {
      navigate(`/ads/${adId}`);
    }
  };

  const handleCloseAdDetailModal = () => {
    setShowAdDetailModal(false);
    setSelectedAdId(null);
  };

  const handleGoToWishPage = () => {
    navigate('/wishes');
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleOpenAdvancedSearch = () => {
    setShowAdvancedSearchModal(true);
  };

  const handleCloseAdvancedSearch = () => {
    setShowAdvancedSearchModal(false);
  };

  const handleToggleFilterPanel = () => {
    setShowFilterPanel(!showFilterPanel);
  };

  const handleOpenCreateAdModal = () => {
    if (!user) {
      navigate('/login');
      return;
    }

    const userPlan = user.plan || 'free';
    const planLimit = PLAN_LIMITS[userPlan];

    // Free plan cannot create ads
    if (userPlan === 'free' || planLimit.maxAds === 0) {
      setLimitReason('免費會員無法刊登廣告，請升級至 Basic 或 Premium 方案');
      setShowLimitModal(true);
      return;
    }

    // Check if user has reached their limit
    if (planLimit.maxAds !== null && usageStats.currentAds >= planLimit.maxAds) {
      setLimitReason(`您已達到 ${userPlan} 方案的廣告數量上限 (${planLimit.maxAds} 則)，請升級至 Premium 方案以刊登更多廣告`);
      setShowLimitModal(true);
      return;
    }

    setEditingAdId(null);
    setShowAdFormModal(true);
  };

  const handleCloseAdFormModal = () => {
    setShowAdFormModal(false);
    setEditingAdId(null);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleAdFormSuccess = async (_newAdId: string) => {
    // Refresh the ads list
    await loadAds();
    // Refresh usage stats
    await fetchUsageStats();
  };

  // Calculate pagination info
  const totalPages = Math.ceil(total / limit);
  const currentPage = page + 1; // Convert to 1-based for display
  const startIndex = page * limit + 1;
  const endIndex = Math.min((page + 1) * limit, total);

  return (
    <div className="ads-list-page">
      <DesktopLayout>
        <div className="ads-list-page__container">
        {/* Header - Single Row */}
        <div className="ads-list-page__header">
          <h1 className="ads-list-page__title">廣告列表</h1>
          <div className="ads-list-page__header-actions">
            {user && (
              <button
                onClick={handleOpenCreateAdModal}
                className="ads-list-page__create-btn"
              >
                + 建立廣告
              </button>
            )}
            <button
              onClick={handleOpenAdvancedSearch}
              className="ads-list-page__advanced-search-btn"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="ads-list-page__btn-icon"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
                />
              </svg>
              <span>進階搜尋</span>
            </button>
            <button
              onClick={handleToggleFilterPanel}
              className={`ads-list-page__filter-btn ${showFilterPanel ? 'ads-list-page__filter-btn--active' : ''}`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="ads-list-page__btn-icon"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M10.5 6h9.75M10.5 6a1.5 1.5 0 1 1-3 0m3 0a1.5 1.5 0 1 0-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-9.75 0h9.75"
                />
              </svg>
              <span>篩選</span>
              <span className="ads-list-page__filter-arrow">{showFilterPanel ? '▲' : '▼'}</span>
            </button>
          </div>
        </div>

        {/* AdvancedSearch Modal (Desktop) */}
        <Modal
          isOpen={showAdvancedSearchModal}
          onClose={handleCloseAdvancedSearch}
          size="large"
          bodyClassName="modal__body--scrollable modal__body--advanced-search"
        >
          <AdvancedSearch onSearch={(params) => {
            handleSearch(params);
            handleCloseAdvancedSearch();
          }} defaultParams={searchParams} />
        </Modal>

        {/* AdDetail Modal (Desktop) */}
        <AdDetailModal
          isOpen={showAdDetailModal}
          onClose={handleCloseAdDetailModal}
          adId={selectedAdId}
          allAdIds={allAdIds}
          onAdChange={setSelectedAdId}
        />

        {/* AdForm Modal (Desktop) */}
        <AdFormModal
          isOpen={showAdFormModal}
          onClose={handleCloseAdFormModal}
          adId={editingAdId}
          onSuccess={handleAdFormSuccess}
        />

        {/* Limit Reached Modal */}
        <Modal
          isOpen={showLimitModal}
          onClose={() => setShowLimitModal(false)}
          size="small"
          title="已達到廣告數量上限"
        >
          <div className="ads-list-page__limit-modal">
            <p className="ads-list-page__limit-message">{limitReason}</p>
            <div className="ads-list-page__limit-actions">
              <button
                onClick={() => setShowLimitModal(false)}
                className="ads-list-page__limit-btn ads-list-page__limit-btn--cancel"
              >
                稍後再說
              </button>
              <button
                onClick={() => {
                  setShowLimitModal(false);
                  navigate('/pricing');
                }}
                className="ads-list-page__limit-btn ads-list-page__limit-btn--upgrade"
              >
                ⭐ 升級會員
              </button>
            </div>
          </div>
        </Modal>

        {/* Main Content - Full Width */}
        <div className="ads-list-page__content-wrapper">
          {/* SearchFacets Panel (Desktop) - Floating */}
          {facets && showFilterPanel && (
            <div className="ads-list-page__facet-panel">
              <SearchFacets
                facets={facets}
                selectedParams={searchParams}
                onFacetClick={handleSearch}
              />
            </div>
          )}

          <div className="ads-list-page__main-content ads-list-page__main-content--full">
            {loading ? (
              <div className="ads-list-page__loading">載入中...</div>
            ) : ads.length === 0 ? (
              <EmptyState
                icon="🔍"
                title="找不到符合條件的廣告"
                description="試試調整篩選條件，或是在許願池發布您的需求，讓服務商主動聯繫您！"
                action={{
                  label: '前往許願池',
                  onClick: handleGoToWishPage,
                }}
              />
            ) : (
              <>
                <div className="ads-list-page__result-info">
                  <span className="ads-list-page__count">
                    找到 {total} 則廣告
                  </span>
                  {total > limit && (
                    <span className="ads-list-page__page-info">
                      顯示 {startIndex} - {endIndex} / 共 {total} 筆（第 {currentPage} / {totalPages} 頁）
                    </span>
                  )}
                </div>

                {/* Top Pagination */}
                {total > limit && (
                  <div className="ads-list-page__pagination ads-list-page__pagination--top">
                    <button
                      onClick={() => handlePageChange(page - 1)}
                      disabled={page === 0}
                      className="pagination-btn"
                    >
                      上一頁
                    </button>

                    <div className="pagination-pages">
                      {Array.from({ length: totalPages }, (_, i) => {
                        const pageNum = i + 1;
                        const showPage =
                          pageNum === 1 ||
                          pageNum === totalPages ||
                          Math.abs(pageNum - currentPage) <= 2;

                        if (!showPage) return null;

                        return (
                          <button
                            key={`top-${i}`}
                            onClick={() => handlePageChange(i)}
                            className={`pagination-page ${page === i ? 'active' : ''}`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>

                    <button
                      onClick={() => handlePageChange(page + 1)}
                      disabled={page >= totalPages - 1}
                      className="pagination-btn"
                    >
                      下一頁
                    </button>
                  </div>
                )}

                <AdCardList ads={ads} loading={loading} onAdClick={handleAdClick} />

                {/* Bottom Pagination */}
                {total > limit && (
                  <div className="ads-list-page__pagination ads-list-page__pagination--bottom">
                    <button
                      onClick={() => handlePageChange(page - 1)}
                      disabled={page === 0}
                      className="pagination-btn"
                    >
                      上一頁
                    </button>

                    <div className="pagination-pages">
                      {Array.from({ length: totalPages }, (_, i) => {
                        const pageNum = i + 1;
                        const showPage =
                          pageNum === 1 ||
                          pageNum === totalPages ||
                          Math.abs(pageNum - currentPage) <= 2;

                        if (!showPage) return null;

                        return (
                          <button
                            key={`bottom-${i}`}
                            onClick={() => handlePageChange(i)}
                            className={`pagination-page ${page === i ? 'active' : ''}`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>

                    <button
                      onClick={() => handlePageChange(page + 1)}
                      disabled={page >= totalPages - 1}
                      className="pagination-btn"
                    >
                      下一頁
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      </DesktopLayout>
    </div>
  );
};

export default AdsListPage;
